import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, TablePagination, IconButton, Stack, Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatValue } from 'src/tools/reportEngine';
import { pickCac } from 'src/utils/cac/pickCac';

const MOVEMENT_CURRENCY_OPTS = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

const getFirstValue = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const formatExtraValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') {
    return value.nombre || value.name || value.label || value.descripcion || '-';
  }
  return String(value);
};

const getDrilldownExtraColumns = (empresaConfig) => {
  const comprobanteInfo = empresaConfig?.comprobante_info || {};
  const ingresoInfo = empresaConfig?.ingreso_info || {};
  const columns = [];

  if (comprobanteInfo.medio_pago === true || ingresoInfo.medio_pago === true) {
    columns.push({
      key: 'medio_pago',
      label: 'Medio de pago',
      getValue: (m) => getFirstValue(m, ['medio_pago', 'medioPago', 'medio_pago_nombre', 'medioPagoNombre']),
    });
  }

  if (comprobanteInfo.detalle === true || ingresoInfo.detalle === true) {
    columns.push({
      key: 'detalle',
      label: 'Detalle',
      getValue: (m) => getFirstValue(m, ['detalle', 'detalle_pago', 'detallePago']),
    });
  }

  return columns;
};

const formatDate = (d) => {
  if (!d) return '-';
  const date = d?.toDate ? d.toDate() : d?.seconds ? new Date(d.seconds * 1000) : new Date(d);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

/**
 * Dialog que muestra los movimientos que componen un cálculo (drill-down).
 * Se abre al clickear un valor en metric_cards, summary_table, o budget_vs_actual.
 */
const DrillDownDialog = ({
  open,
  onClose,
  movimientos = [],
  titulo = '',
  displayCurrency = 'ARS',
  cacModo = 'legacy',
  empresaConfig = null,
}) => {
  const [page, setPage] = useState(0);
  const [imgPreview, setImgPreview] = useState({ open: false, url: null });
  const [showUsd, setShowUsd] = useState(false);
  const [showCac, setShowCac] = useState(false);
  const [nativeCurrencyFilter, setNativeCurrencyFilter] = useState('ALL');
  const rowsPerPage = 25;
  const extraColumns = useMemo(() => getDrilldownExtraColumns(empresaConfig), [empresaConfig]);

  const openImg = (url) => setImgPreview({ open: true, url });
  const closeImg = () => setImgPreview({ open: false, url: null });

  // Cerrar preview si se cierra el dialog padre
  useEffect(() => {
    if (!open) closeImg();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setShowUsd(false);
    setShowCac(false);
    setNativeCurrencyFilter('ALL');
    setPage(0);
  }, [open, displayCurrency]);

  const getAmountByCurrency = (mov, currency) => {
    const total = Number(mov?.total ?? mov?.monto ?? 0);
    const monedaMov = (mov?.moneda || 'ARS').toUpperCase();
    const eqTotal = mov?.equivalencias?.total || {};

    if (currency === 'ARS') {
      if (monedaMov === 'ARS') return total;
      if (eqTotal.ars != null && !isNaN(eqTotal.ars)) return Number(eqTotal.ars);
      return total;
    }

    if (currency === 'USD') {
      if (monedaMov === 'USD') return total;
      if (monedaMov === 'ARS' && Number(mov?.dolar_referencia) > 0) {
        return total / Number(mov.dolar_referencia);
      }
      if (mov?.total_dolar != null && !isNaN(mov.total_dolar)) return Number(mov.total_dolar);
      if (eqTotal.usd_blue != null && !isNaN(eqTotal.usd_blue)) return Number(eqTotal.usd_blue);
      return null;
    }

    if (currency === 'CAC') {
      if (monedaMov === 'CAC') return total;
      const cac = pickCac(eqTotal.cac, cacModo);
      if (cac != null && !isNaN(cac)) return Number(cac);
      return null;
    }

    return total;
  };

  const formatCurrencyValue = (amount, currency) => {
    if (amount == null || isNaN(amount)) return '-';
    if (currency === 'ARS') {
      return formatValue(amount, 'currency', 'ARS', MOVEMENT_CURRENCY_OPTS);
    }
    if (currency === 'USD') {
      return Number(amount).toLocaleString('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }
    if (currency === 'CAC') {
      return Number(amount).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return String(amount);
  };

  const sortedMovs = useMemo(() => {
    const filtered = nativeCurrencyFilter === 'ALL'
      ? movimientos
      : movimientos.filter((m) => String(m.moneda || displayCurrency).toUpperCase() === nativeCurrencyFilter);

    return [...filtered].sort((a, b) => {
      const da = a.fecha_factura?.toDate?.() || a.fecha_factura?.seconds ? new Date(a.fecha_factura.seconds * 1000) : new Date(a.fecha_factura || a.fecha || 0);
      const db = b.fecha_factura?.toDate?.() || b.fecha_factura?.seconds ? new Date(b.fecha_factura.seconds * 1000) : new Date(b.fecha_factura || b.fecha || 0);
      return db.getTime() - da.getTime();
    });
  }, [movimientos, nativeCurrencyFilter, displayCurrency]);

  const visibleRows = useMemo(
    () => sortedMovs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedMovs, page],
  );

  const acumuladoById = useMemo(() => {
    const ordered = [...sortedMovs].reverse(); // cronológico para acumulado
    const map = {};
    let running = 0;

    for (const m of ordered) {
      const amount = nativeCurrencyFilter === 'ALL'
        ? Number(m?.total ?? m?.monto ?? 0)
        : getAmountByCurrency(m, nativeCurrencyFilter);
      if (amount != null && !isNaN(amount)) {
        running += m.type === 'egreso' ? -Math.abs(Number(amount)) : Math.abs(Number(amount));
      }
      map[m.id] = running;
    }

    return map;
  }, [sortedMovs, nativeCurrencyFilter, displayCurrency]);

  const totalesPorMoneda = useMemo(() => {
    const map = {};
    for (const m of movimientos) {
      const cur = m.moneda || displayCurrency;
      const amount = Number(m.total ?? m.monto ?? 0);
      if (isNaN(amount)) continue;
      map[cur] = (map[cur] || 0) + (m.type === 'egreso' ? -Math.abs(amount) : Math.abs(amount));
    }
    return map;
  }, [movimientos, displayCurrency]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              {titulo || 'Movimientos del cálculo'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {sortedMovs.length} de {movimientos.length} movimientos
              </Typography>
              <Chip
                label="Todos"
                size="small"
                color="default"
                variant={nativeCurrencyFilter === 'ALL' ? 'filled' : 'outlined'}
                onClick={() => {
                  setNativeCurrencyFilter('ALL');
                  setPage(0);
                }}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="Movimientos ARS"
                size="small"
                color="primary"
                variant={nativeCurrencyFilter === 'ARS' ? 'filled' : 'outlined'}
                onClick={() => {
                  setNativeCurrencyFilter('ARS');
                  setPage(0);
                }}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="Movimientos USD"
                size="small"
                color="success"
                variant={nativeCurrencyFilter === 'USD' ? 'filled' : 'outlined'}
                onClick={() => {
                  setNativeCurrencyFilter('USD');
                  setPage(0);
                }}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="Equiv. USD"
                size="small"
                color="success"
                variant={showUsd ? 'filled' : 'outlined'}
                onClick={() => setShowUsd((prev) => !prev)}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="CAC"
                size="small"
                color="secondary"
                variant={showCac ? 'filled' : 'outlined'}
                onClick={() => setShowCac((prev) => !prev)}
                sx={{ cursor: 'pointer' }}
              />
              {Object.entries(totalesPorMoneda).map(([cur, total]) => (
                <Chip
                  key={cur}
                  label={`Saldo ${formatValue(total, 'currency', cur)}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Categoría</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Proveedor</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Proyecto</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Subcategoría</TableCell>
                {extraColumns.map((col) => (
                  <TableCell key={col.key} sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>
                    {col.label}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }} align="right">
                  Monto ({nativeCurrencyFilter === 'ALL' ? 'original' : nativeCurrencyFilter})
                </TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }} align="right">
                  Saldo acum. ({nativeCurrencyFilter === 'ALL' ? 'original' : nativeCurrencyFilter})
                </TableCell>
                {showUsd && <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100', color: 'success.main' }} align="right">Equiv. USD</TableCell>}
                {showCac && <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100', color: 'secondary.main' }} align="right">CAC</TableCell>}
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>Observación</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: 'grey.100', textAlign: 'center' }}>Archivo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((m, idx) => (
                <TableRow key={m.id || idx} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(m.fecha_factura || m.fecha)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={m.type === 'egreso' ? 'Egreso' : 'Ingreso'}
                      size="small"
                      color={m.type === 'egreso' ? 'error' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{m.categoria || '-'}</TableCell>
                  <TableCell>{m.nombre_proveedor || '-'}</TableCell>
                  <TableCell>{m.proyecto || '-'}</TableCell>
                  <TableCell>{m.subcategoria || '-'}</TableCell>
                  {extraColumns.map((col) => (
                    <TableCell key={col.key}>
                      <Typography
                        variant="body2"
                        sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {formatExtraValue(col.getValue(m))}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {formatCurrencyValue(
                      nativeCurrencyFilter === 'ALL'
                        ? Number(m?.total ?? m?.monto ?? 0)
                        : getAmountByCurrency(m, nativeCurrencyFilter),
                      nativeCurrencyFilter === 'ALL' ? (m.moneda || displayCurrency) : nativeCurrencyFilter,
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontWeight: 500 }}>
                    {formatCurrencyValue(
                      acumuladoById[m.id],
                      nativeCurrencyFilter === 'ALL' ? (m.moneda || displayCurrency) : nativeCurrencyFilter,
                    )}
                  </TableCell>
                  {showUsd && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'success.main' }}>
                      {formatCurrencyValue(getAmountByCurrency(m, 'USD'), 'USD')}
                    </TableCell>
                  )}
                  {showCac && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'secondary.main' }}>
                      {formatCurrencyValue(getAmountByCurrency(m, 'CAC'), 'CAC')}
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {m.observacion || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {m.url_imagen && (
                      <IconButton
                        size="small"
                        onClick={() => openImg(m.url_imagen)}
                        title="Ver archivo"
                      >
                        <ImageIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10 + extraColumns.length + (showUsd ? 1 : 0) + (showCac ? 1 : 0)} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      Sin movimientos
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {sortedMovs.length > rowsPerPage && (
          <TablePagination
            component="div"
            count={sortedMovs.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      {/* Dialog para preview de imagen */}
      <Dialog open={imgPreview.open} onClose={closeImg} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Archivo adjunto
          <Box>
            {imgPreview.url && (
              <IconButton
                size="small"
                component="a"
                href={imgPreview.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mr: 1 }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" onClick={closeImg}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {imgPreview.url ? (
            imgPreview.url.toLowerCase().includes('.pdf') ? (
              <Box sx={{ height: '70vh' }}>
                <iframe
                  src={imgPreview.url}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="PDF Preview"
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={imgPreview.url}
                  alt="Preview"
                  sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              </Box>
            )
          ) : (
            <Typography color="text.secondary">Cargando...</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default DrillDownDialog;
